import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { StudentPurchaseHistory } from "@/components/StudentPurchaseHistory";
import { BillingProfiles } from "@/components/BillingProfiles";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { LockKeyhole, Tag, Check, Trash2, Send } from "lucide-react";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/format";
import { useAuth } from "@/lib/auth";
import { Badge } from "@/components/ui/badge";

export const ONBOARDING_LABEL = {
  not_started: "Not Started",
  in_progress: "In Progress",
  onboarded: "Onboarded",
  failed: "Failed",
};
export const ONBOARDING_STYLE = {
  not_started: "bg-slate-100 text-slate-700 border-slate-200",
  in_progress: "bg-amber-50 text-amber-800 border-amber-200",
  onboarded: "bg-emerald-50 text-emerald-800 border-emerald-200",
  failed: "bg-rose-50 text-rose-800 border-rose-200",
};

const FIELDS = [["name", "Full name"], ["email", "Email"], ["phone", "Phone"], ["company", "Company"], ["country", "Country (ISO)"], ["tax_id", "Tax ID"], ["address", "Address"]];
const blank = { name: "", email: "", phone: "", company: "", country: "MY", tax_id: "", address: "", student_ref: "", notes: "", tag_ids: [] };

export const StudentProfile = ({ student, open, onOpenChange, tags, onSaved }) => {
  const { user } = useAuth();
  const isSuper = user?.role === "superadmin";
  const [form, setForm] = useState(blank);
  const [tab, setTab] = useState("details");
  const [refMatch, setRefMatch] = useState(null);
  useEffect(() => { setTab("details"); }, [student?.id]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  useEffect(() => { if (open) { setForm({ ...blank, ...student }); setError(""); setRefMatch(null); } }, [student, open]);
  const linked = !!student?.elitebase_id;

  // Live duplicate check for the student_ref field
  useEffect(() => {
    const ref = (form.student_ref || "").trim();
    if (!ref) { setRefMatch(null); return; }
    const c = new AbortController();
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/customers/lookup-ref", {
          params: { ref, exclude_id: student?.id }, signal: c.signal,
        });
        if (!c.signal.aborted) setRefMatch(data.match);
      } catch (_) { /* ignore */ }
    }, 250);
    return () => { clearTimeout(t); c.abort(); };
  }, [form.student_ref, student?.id]);

  const save = async () => {
    setSaving(true); setError("");
    try {
      let payload = { notes: form.notes, tag_ids: form.tag_ids };
      if (!linked) FIELDS.forEach(([k]) => { payload[k] = form[k]?.trim() || null; });
      payload.student_ref = form.student_ref?.trim() || null;
      if (student) {
        payload.expected_revision = student.revision;
        await api.patch(`/customers/${student.id}`, payload);
      } else await api.post("/customers", { ...payload, roles: ["buyer", "learner"] });
      toast.success(student ? "Student updated" : "Student created"); onSaved();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    setDeleting(true);
    try {
      await api.delete(`/customers/${student.id}`);
      toast.success("Student deleted");
      setConfirmDelete(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    } finally { setDeleting(false); }
  };
  return <Sheet open={open} onOpenChange={(v) => { if (!saving) onOpenChange(v); }}><SheetContent className={`w-full ${tab === "details" ? "sm:max-w-xl" : "sm:max-w-4xl"} overflow-y-auto`} data-testid="student-profile">
    <SheetHeader><SheetTitle data-testid="student-profile-title">{student ? student.name : "New student"}</SheetTitle><SheetDescription>One student record, connected to your Sales and documents.</SheetDescription></SheetHeader>
    {student && <Tabs value={tab} onValueChange={setTab} className="mt-5"><TabsList><TabsTrigger value="details" data-testid="student-tab-details">Profile</TabsTrigger><TabsTrigger value="history" data-testid="student-tab-history">Purchase history</TabsTrigger><TabsTrigger value="billing" data-testid="student-tab-billing">Billing profiles</TabsTrigger></TabsList></Tabs>}
    {student && tab === "history" && <div className="mt-5"><StudentPurchaseHistory studentId={student.id} /></div>}
    {student && tab === "billing" && <div className="mt-5"><BillingProfiles studentId={student.id} /></div>}
    {tab === "details" && <div className="space-y-5 mt-6">
      <div className={`p-3 rounded border text-xs ${linked ? "border-blue-200 bg-blue-50 text-blue-800" : "border-slate-200 bg-slate-50 text-slate-600"}`} data-testid="student-source-status">
        {linked ? <><LockKeyhole className="inline w-3.5 h-3.5 mr-1" />EliteBase owns profile details · v{student.source_profile_version}<div className="font-mono mt-1 break-all">{student.elitebase_id}</div><div className="mt-1">Last approved sync: {fmtDateTime(student.synced_at)}</div></> : "Local student · link to EliteBase through the review queue"}
      </div>
      {student && <ElitebaseOnboardingSection studentId={student.id} initial={student.elitebase_onboarding} />}
      <fieldset disabled={saving} className="space-y-5">
        <div>
          <Label htmlFor="student-student-ref" className="text-xs text-slate-500">Student ID / CRM Reference</Label>
          <Input
            id="student-student-ref"
            data-testid="student-student-ref"
            value={form.student_ref || ""}
            onChange={(e) => setForm({ ...form, student_ref: e.target.value })}
            placeholder="e.g. CRM-001234"
            maxLength={80}
            aria-describedby="student-student-ref-hint"
            aria-invalid={!!refMatch}
          />
          <p id="student-student-ref-hint" className="text-xs text-slate-500 mt-1">Unique reference from your CRM. Must not clash with another student.</p>
          {refMatch && (
            <p role="alert" className="mt-2 text-xs text-rose-800 border border-rose-200 bg-rose-50 rounded p-2" data-testid="student-student-ref-conflict">
              This ID is already used by <span className="font-mono font-semibold">{refMatch.name}</span>
              {refMatch.email ? <> · {refMatch.email}</> : null}. Pick a different ID or update the existing record.
            </p>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">{FIELDS.map(([key, label]) => <div key={key} className={key === "address" ? "col-span-2" : ""}>
          <Label htmlFor={`student-${key}`} className="text-xs text-slate-500">{label}</Label>
          <Input id={`student-${key}`} data-testid={`student-${key}`} type={key === "email" ? "email" : "text"} disabled={linked} value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
        </div>)}</div>
        {Object.keys(student?.profile_details || {}).length > 0 && <div className="border-t pt-3" data-testid="student-additional-details"><h3 className="text-xs font-semibold mb-2">EliteBase onboarding details</h3>{Object.entries(student.profile_details).map(([k, v]) => <div className="flex justify-between gap-4 py-1 text-sm" key={k}><span className="text-slate-500">{k}</span><span className="text-right break-words">{v || "—"}</span></div>)}</div>}
        <div><Label className="text-xs flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" /> Team tags</Label><p className="text-xs text-slate-500 mt-1 mb-2">Local labels for analysis; source updates never overwrite them.</p>
          <div className="flex flex-wrap gap-2" data-testid="student-tag-choices">{tags.filter((t) => t.active || form.tag_ids.includes(t.id)).map((t) => <Button key={t.id} size="sm" variant="outline" aria-pressed={form.tag_ids.includes(t.id)} className={form.tag_ids.includes(t.id) ? "bg-blue-50 border-blue-300 text-blue-800" : ""} data-testid={`student-tag-${t.id}`} onClick={() => setForm({ ...form, tag_ids: form.tag_ids.includes(t.id) ? form.tag_ids.filter((id) => id !== t.id) : [...form.tag_ids, t.id] })}>
            {form.tag_ids.includes(t.id) && <Check className="w-3 h-3 mr-1" />}{t.name}{!t.active && " (archived)"}</Button>)}
            {tags.length === 0 && <span className="text-xs text-slate-500">Create your first tag using Manage tags.</span>}
          </div>
        </div>
        <div><Label htmlFor="student-notes" className="text-xs">Internal notes</Label><Textarea id="student-notes" data-testid="student-notes" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} /></div>
        {student && <div className="text-xs text-slate-500" data-testid="student-existing-roles">Existing roles: {student.roles.join(", ")} · Finance references are preserved</div>}
      </fieldset>
      {error && <div role="alert" className="text-sm text-rose-700" data-testid="student-profile-error">{error}</div>}
      <div className="flex justify-between items-center gap-2 border-t pt-4">
        <div>
          {student && isSuper && !linked && (
            <Button
              variant="outline"
              className="text-rose-700 border-rose-200 hover:bg-rose-50 hover:text-rose-800"
              disabled={saving || deleting}
              onClick={() => setConfirmDelete(true)}
              data-testid="student-delete-button"
            >
              <Trash2 className="w-4 h-4 mr-1.5" />Delete student
            </Button>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" disabled={saving} onClick={() => onOpenChange(false)} data-testid="student-profile-cancel">Cancel</Button>
          <Button disabled={saving || !!refMatch || (!linked && !form.name?.trim())} onClick={save} data-testid="student-save">{saving ? "Saving…" : "Save student"}</Button>
        </div>
      </div>
    </div>}
    <AlertDialog open={confirmDelete} onOpenChange={(v) => { if (!deleting) setConfirmDelete(v); }}>
      <AlertDialogContent data-testid="student-delete-confirm">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {student?.name}?</AlertDialogTitle>
          <AlertDialogDescription>
            This removes the student permanently. It only succeeds if the student has no sales, invoices, pro-formas, receipts, or gateway transactions linked. This action is audit-logged.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleting} data-testid="student-delete-cancel">Keep student</AlertDialogCancel>
          <AlertDialogAction
            disabled={deleting}
            onClick={(e) => { e.preventDefault(); remove(); }}
            className="bg-rose-600 hover:bg-rose-700 focus:ring-rose-600"
            data-testid="student-delete-confirm-action"
          >
            {deleting ? "Deleting…" : "Delete student"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </SheetContent></Sheet>;
};


function ElitebaseOnboardingSection({ studentId, initial }) {
  const [state, setState] = useState(initial || { status: "not_started" });
  const [sending, setSending] = useState(false);

  useEffect(() => { setState(initial || { status: "not_started" }); }, [studentId, initial]);

  const refresh = async () => {
    try {
      const { data } = await api.get(`/customers/${studentId}`);
      setState(data.elitebase_onboarding || { status: "not_started" });
    } catch { /* soft refresh; ignore */ }
  };

  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/elitebase/students/${studentId}/send-onboarding`);
      setState(data.elitebase_onboarding || { status: "in_progress" });
      const s = (data.elitebase_onboarding || {}).status;
      if (s === "in_progress") toast.success("Onboarding request sent to EliteBase");
      else if (s === "failed") toast.error(data.elitebase_onboarding.last_message || "Onboarding could not be sent");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSending(false); }
  };

  const status = state?.status || "not_started";
  const canSend = status === "not_started" || status === "failed";

  return (
    <div className="p-4 rounded border border-slate-200 bg-white space-y-3" data-testid="elitebase-onboarding-section">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs font-semibold uppercase text-slate-500">EliteBase Onboarding</div>
          <div className="mt-1 flex items-center gap-2">
            <Badge variant="outline" className={ONBOARDING_STYLE[status]} data-testid="elitebase-onboarding-status">
              {ONBOARDING_LABEL[status] || status}
            </Badge>
            {status === "in_progress" && <span className="text-xs text-slate-500">Waiting for EliteBase to confirm…</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={refresh} data-testid="elitebase-onboarding-refresh">Refresh</Button>
          {canSend && (
            <Button size="sm" onClick={send} disabled={sending} data-testid="elitebase-onboarding-send">
              <Send className="w-3.5 h-3.5 mr-1.5" />
              {sending ? "Sending…" : (status === "failed" ? "Retry send" : "Send to Elitebase")}
            </Button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
        <div className="text-slate-500">Last attempt</div>
        <div className="font-mono text-slate-700" data-testid="elitebase-onboarding-last-attempt">{state?.last_attempt_at ? fmtDateTime(state.last_attempt_at) : "—"}</div>
        <div className="text-slate-500">Onboarded at</div>
        <div className="font-mono text-slate-700" data-testid="elitebase-onboarding-onboarded-at">{state?.onboarded_at ? fmtDateTime(state.onboarded_at) : "—"}</div>
        <div className="text-slate-500">Order ID</div>
        <div className="font-mono text-slate-700">{state?.last_order_id || "—"}</div>
      </div>
      {state?.last_message && (
        <div className={`text-xs rounded p-2 border ${status === "failed" ? "border-rose-200 bg-rose-50 text-rose-800" : "border-slate-200 bg-slate-50 text-slate-700"}`} data-testid="elitebase-onboarding-message">
          {state.last_message}
        </div>
      )}
    </div>
  );
}
